<template>
  <section aria-roledescription="carousel" :aria-label="props.carouselTitle">
    <v-carousel color="#0d2154" :interval="carouselInterval" :cycle="carouselCycle" :height="props.carouselHeight"
      hide-delimiter-background hide-delimiters style="background-color: #FFFFFF;">

      <template v-slot:prev="{ props }">
        <div style="background-color: #EEEEEE; 
          position: absolute; 
          width: 55px; 
          height: 20px; 
          bottom: 0px; 
          left: 5px; 
          z-index: 100;
          border-top-left-radius: 25px;
          border-bottom-left-radius: 25px;
          color: #6E6E6E;">
          <v-btn color="#6E6E6E" variant="plain" @click="props.onClick" icon
            style="position: absolute; bottom: -6px; left: -5px; z-index: 102;" 
            aria-label="Previous Slide"
            tabindex="0"
            @keydown.enter="props.onClick"
            @keydown.space.prevent="props.onClick"
            size="x-small">
            <v-icon size="x-large" icon="mdi-arrow-left-bold-circle" />
          </v-btn>
        </div>
      </template>

      <template v-slot:next="{ props }">
        <div style="background-color: #EEEEEE; 
          position: absolute; 
          width: 60px; 
          height: 20px; 
          bottom: 0px; 
          left: 33px; 
          z-index: 100;
          border-top-right-radius: 25px;
          border-bottom-right-radius: 25px;
          color: #6E6E6E;">
          <v-btn color="#6E6E6E" variant="plain" @click="carouselCycle = !carouselCycle" icon
            style="position: absolute; bottom: -6px; left: 0px; z-index: 102;" 
            :aria-label="carouselCycle ? 'Pause automatic slide show' : 'Resume automatic slide show'"
            tabindex="0"
            @keydown.enter="carouselCycle = !carouselCycle"
            @keydown.space.prevent="carouselCycle = !carouselCycle"
            size="x-small">
            <v-icon size="x-large" :icon="carouselPauseResume" />
          </v-btn>

          <v-btn color="#6E6E6E" variant="plain" @click="props.onClick" icon
            style="position: absolute; bottom: -6px; right: -5px; z-index: 102;" 
            aria-label="Next Slide" 
            tabindex="0"
            @keydown.enter="props.onClick"
            @keydown.space.prevent="props.onClick"
            size="x-small">
            <v-icon size="x-large" icon="mdi-arrow-right-bold-circle" />
          </v-btn>
        </div>
      </template>


      <v-carousel-item v-for="(item, index) in props.carouselItems" :key="index" fill :src="getImgUrl(item)" height="100%" width="100%" role="group"
        :aria-label="`Slide ${index + 1} of ${props.carouselItems.length}: ${getCarouselItemAlt(item)}`" aria-roledescription="slide" @mouseover="carouselCycle = false"
        @mouseout="carouselCycle = true" :alt="getCarouselItemAlt(item)">

          <div v-if="item.customLanguageContent !== undefined" class="fill-height fill-width">
            
            <div v-if="item.isClickable" class=""
              @click="getCustomClickEvent(item)" 
              @keydown.enter="getCustomClickEvent(item)"
              @keydown.space.prevent="getCustomClickEvent(item)"
              tabindex="0"
              role="button"
              :aria-label="getItemAriaLabel(item)"
              style="cursor: pointer; width: 100%; height: 100%;">
            </div>

        </div>
        <div
          v-else-if="item.customLanguageContent === undefined" class="fill-height fill-width">
          
            <div v-if="item.isClickable" class="carousel-heading ma-0 pa-0"
              @click="systemStore.routerTo(item.route)"
              @keydown.enter="systemStore.routerTo(item.route)"
              @keydown.space.prevent="systemStore.routerTo(item.route)"
              tabindex="0"
              role="button"
              :aria-label="getItemAriaLabel(item)"
              style="cursor: pointer; width: 100%; height: 100%;">
              <LanguagePlaceholder is-html :default-text="item.title" />
            </div>

            <div v-else-if="!item.isClickable" class="general-carousel-heading" style="width: 100%; height: 100%;">
              {{ item.title }}
            </div>

        </div>

      </v-carousel-item>
    </v-carousel>
  </section>
</template>

<script setup>
import { ref, watch } from 'vue'
import { useSystemStore } from '@/stores/system'
import LanguagePlaceholder from '@/components/LanguagePlaceholder.vue'

const systemStore = useSystemStore()

const carouselInterval = ref(4000)
const carouselPauseResume = ref('mdi-pause-circle')
const carouselCycle = ref(true)

const props = defineProps({
  carouselHeight: { type: String, default: '250' },
  carouselTitle: { type: String, default: '' },
  carouselItems: { type: Array, default: [] }
})

watch(
  carouselCycle,
  () => {
    carouselPauseResume.value = carouselCycle.value ? 'mdi-pause-circle' : 'mdi-play-circle'
  }
)

const getImgUrl = (item) => {

  var lng = systemStore.selectedLanguage
  
    if (item.customLanguageContent !== undefined){
      if (item.customLanguageContent[lng] !== undefined) {
        return item.customLanguageContent[lng].imgUrl
      } else {
        return item.customLanguageContent['en'].imgUrl // Fallback to English if the selected language is not available
      }
    } else {
      return item.url
    }

}

const getCustomClickEvent = (item) => {

  var lng = systemStore.selectedLanguage  
  var itm = item.customLanguageContent[lng]

  if (itm !== undefined)
    return itm.clickEvent()
  else
    return item.customLanguageContent['en'].clickEvent() // Fallback to English if the selected language is not available

}

const getCarouselItemAlt = (item) => {
  const lng = systemStore.selectedLanguage
  const localizedAlt = item?.customLanguageContent?.[lng]?.alt
    ?? item?.customLanguageContent?.['en']?.alt
  if (localizedAlt) return localizedAlt
  if (item?.ariaLabel && item.ariaLabel !== 'Carousel image') return item.ariaLabel
  if (item?.title && item.title.trim()) return item.title
  return 'Promotional carousel image'
}

const getItemAriaLabel = (item) => {
  const fallback = item?.title ? `Click to view ${item.title}` : 'Activate carousel item'

  const lng = systemStore.selectedLanguage
  const localized = item?.customLanguageContent?.[lng]?.ariaLabel
    ?? item?.customLanguageContent?.['en']?.ariaLabel

  return localized ?? item?.ariaLabel ?? fallback
}

</script>

<style scoped>
/* WCAG 2.4.7 Focus Visible — all focusable elements within carousel */
:deep([role="button"]:focus-visible),
:deep([tabindex="0"]:focus-visible) {
    outline: 3px solid #FFD700 !important;
    outline-offset: 2px !important;
    box-shadow: 0 0 0 6px rgba(255, 215, 0, 0.4) !important;
}

/* Focus indicator for carousel slide items (role=group) when focused */
:deep(.v-carousel-item:focus-visible),
:deep([role="group"]:focus-visible) {
    outline: 3px solid #FFD700 !important;
    outline-offset: -3px !important;
    box-shadow: inset 0 0 0 3px rgba(255, 215, 0, 0.6) !important;
}

/* Focus indicator for prev/next carousel buttons */
:deep(.v-btn:focus-visible) {
    outline: 3px solid #FFD700 !important;
    outline-offset: 3px !important;
    box-shadow: 0 0 0 6px rgba(255, 215, 0, 0.5) !important;
}
</style>